import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import api from '../services/api';
import ExportExcel from '../components/ExportExcel';
import useEstMobile from '../hooks/useEstMobile';
import { LABELS_ROLES } from '../constants/roles';
import MenuProfil from '../components/MenuProfil';
import {
  IconeHorloge, IconePanier, IconeInstitution, IconeCrayon, IconePlume,
  IconeDocument, IconeCompte, IconeListe, IconeNote, IconeAvion, IconeRecu,
  IconeRafraichir, IconeDossier, IconeValide, IconeRefuse, IconeCloche, IconeFeu,
  IconeFermer,
} from '../components/IconesSVG';

const ROLES_HOLDING = ['preparateur_memo_holding', 'assistante_pdg', 'pdg'];

const CONFIG_ROLE = {
  acheteur: {
    label: 'Acheteur', icon: IconePanier, color: '#C0392B', colorLight: '#FDEDEC',
    statut_attente: 'en_controle_terrain',
    description: 'Vous validez les achats et bons de commande avant transmission',
    tips: ['Verifiez les devis joints', 'Controlez le montant estime', 'Validez le fournisseur propose'],
  },
  direction_achats: {
    label: 'Direction Achats', icon: IconeInstitution, color: '#2980B9', colorLight: '#EAF4FB',
    statut_attente: 'en_memo_filiale',
    description: 'Supervision et validation direction des achats',
    tips: ['Verifiez la conformite budgetaire', 'Controlez les procedures', 'Validez la strategie achat'],
  },
  preparateur_memo_filiale: {
    label: 'Preparateur Memo Filiale', icon: IconeCrayon, color: '#27AE60', colorLight: '#EAFAF1',
    statut_attente: 'en_memo_filiale',
    description: 'Vous preparez les memos filiale pour validation DG',
    tips: ['Redigez le memo en Word/PDF', 'Verifiez les montants', 'Uploadez le PDF final'],
  },
  dg_filiale: {
    label: 'DG Filiale', icon: IconePlume, color: '#C0392B', colorLight: '#FDEDEC',
    statut_attente: 'en_signature_dg_filiale',
    description: 'Vous signez les memos filiale et transmettez a la Holding',
    tips: ['Lisez le memo filiale', 'Verifiez la synthese', 'Signez pour transmettre a la Holding'],
  },
  preparateur_memo_holding: {
    label: 'Preparateur Memo Holding', icon: IconeDocument, color: '#2980B9', colorLight: '#EAF4FB',
    statut_attente: 'envoye_holding',
    description: 'Vous preparez les memos Holding pour le PDG',
    tips: ['Compilez les memos filiales', 'Redigez la synthese Holding', 'Transmettez au PDG'],
  },
  assistante_pdg: {
    label: 'Assistante Direction PDG', icon: IconeCompte, color: '#6C3483', colorLight: '#F5EEF8',
    statut_attente: 'en_validation_assistante_pdg',
    description: 'Vous validez les memos Holding avant transmission au PDG',
    tips: ['Verifiez le memo Holding', 'Assurez-vous que tout est conforme', 'Transmettez au PDG pour signature finale'],
  },
  responsable: {
    label: 'Responsable Zone', icon: IconeCompte, color: '#E67E22', colorLight: '#FDF2E9',
    statut_attente: 'en_attente_responsable',
    description: 'Vous validez les demandes de votre zone avant transmission a lacheteur',
    tips: ['Verifiez la demande', 'Assurez-vous de la pertinence', 'Approuvez ou rejetez'],
  },
  responsable_service: {
    label: 'Responsable de Service', icon: IconeCompte, color: '#C0392B', colorLight: '#FDEDEC',
    statut_attente: 'en_controle_terrain',
    description: 'Vous validez les demandes de votre service',
    tips: ['Verifiez la pertinence du besoin', 'Controlez le budget service', 'Validez ou rejetez'],
  },
};

const STATUTS = {
  en_controle_terrain:     { label: 'En attente',        color: '#9A7D0A', bg: '#FEF9E7' },
  en_memo_filiale:         { label: 'Memo filiale',       color: '#9A7D0A', bg: '#FEF9E7' },
  en_signature_dg_filiale: { label: 'Chez DG',            color: '#1A5E8A', bg: '#EAF4FB' },
  envoye_holding:          { label: 'Chez Holding',       color: '#1A5E8A', bg: '#EAF4FB' },
  dans_parapheur_pdg:      { label: 'Chez PDG',           color: '#6C3483', bg: '#F5EEF8' },
  solde:                   { label: 'Approuve',           color: '#1E8449', bg: '#EAFAF1' },
  en_correction:           { label: 'A corriger',         color: '#C0392B', bg: '#FDEDEC' },
  en_correction_memo:      { label: 'Memo a corriger',    color: '#C0392B', bg: '#FDEDEC' },
  en_correction_holding:   { label: 'Holding a corriger', color: '#C0392B', bg: '#FDEDEC' },
  en_correction_pdg:       { label: 'PDG a corriger',     color: '#C0392B', bg: '#FDEDEC' },
  rejete:                  { label: 'Rejete',             color: '#96281B', bg: '#FDEDEC' },
  brouillon:               { label: 'Brouillon',          color: '#888',    bg: '#F2F3F4' },
};

const TYPES = {
  expression_besoin: { label: 'Fiche EB',       icon: IconeListe, color: '#2980B9' },
  bon_commande:      { label: 'Bon commande',   icon: IconeNote, color: '#8E44AD' },
  frais_mission:     { label: 'Frais mission',  icon: IconeAvion, color: '#E67E22' },
  facture:           { label: 'Facture',        icon: IconeRecu, color: '#16A085' },
  regularisation:    { label: 'Regularisation', icon: IconeRafraichir, color: '#935116' },
};

export default function DashboardActeur() {
  const estMobile = useEstMobile();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [pochettes, setPochettes] = useState([]);
  const [enAttente, setEnAttente] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onglet, setOnglet] = useState('attente');
  const [rechercheTexte, setRechercheTexte] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [filtreFiliale, setFiltreFiliale] = useState('');
  const [filtrePriorite, setFiltrePriorite] = useState('');
  const [filtreDateDebut, setFiltreDateDebut] = useState('');
  const [filtreDateFin, setFiltreDateFin] = useState('');

  // Le backend (authService.profilPublic) renvoie un role unique, expose a la
  // fois en `role` et en `roles: [{ role, filiale }]` pour l'ancien format.
  const roleUser = user?.roles?.[0]?.role || user?.role;
  const config = CONFIG_ROLE[roleUser] || { label: LABELS_ROLES[roleUser] || roleUser, icon: IconeCompte, color: '#666', colorLight: '#F0F0F0', statut_attente: '', description: '', tips: [] };

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    try {
      setLoading(true);
      const [attenteRes, allRes] = await Promise.all([
        api.get('/demandes/a-traiter'),
        api.get('/demandes'),
      ]);
      setEnAttente(attenteRes.data?.data || attenteRes.data || []);
      setPochettes(allRes.data?.data || allRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const formatMontant = (m) => m ? Number(m).toLocaleString('fr-FR') + ' F' : '—';

  // Pour les roles Holding (isGlobal) : utiliser /demandes avec tous les statuts
  // Pour les autres roles : filtrer par statut du role
  const pochettesDuRole = ROLES_HOLDING.includes(roleUser)
    ? pochettes.filter(p =>
        enAttente.some(ea => ea.id === p.id) ||
        p.statut === 'solde' ||
        p.statut === 'rejete' ||
        p.statut === config.statut_attente
      )
    : pochettes.filter(p =>
        p.statut === config.statut_attente ||
        p.statut === 'solde' ||
        p.statut === 'rejete' ||
        enAttente.some(ea => ea.id === p.id)
      );

  const docsList = onglet === 'attente' ? enAttente : pochettesDuRole;

  const filialesDisponibles = Array.from(
    new Map(
      docsList.filter(p => p.filiale).map(p => [p.filiale.id || p.filiale._id, p.filiale])
    ).values()
  );

  const docsFiltres = docsList.filter(p => {
    if (rechercheTexte) {
      const texte = (p.reference + ' ' + (p.titre || '') + ' ' + (p.demandeur?.nom || '') + ' ' + (p.demandeur?.prenom || '')).toLowerCase();
      if (!texte.includes(rechercheTexte.toLowerCase())) return false;
    }
    if (filtreStatut && p.statut !== filtreStatut) return false;
    if (filtreFiliale && (p.filiale?.id || p.filiale?._id) !== filtreFiliale) return false;
    if (filtrePriorite && p.priorite !== filtrePriorite) return false;
    if (filtreDateDebut && new Date(p.created_at) < new Date(filtreDateDebut)) return false;
    if (filtreDateFin && new Date(p.created_at) > new Date(filtreDateFin + 'T23:59:59')) return false;
    return true;
  });

  const reinitialiserFiltres = () => {
    setRechercheTexte(''); setFiltreStatut(''); setFiltreFiliale('');
    setFiltrePriorite(''); setFiltreDateDebut(''); setFiltreDateFin('');
  };

  const filtresActifs = !!(rechercheTexte || filtreStatut || filtreFiliale || filtrePriorite || filtreDateDebut || filtreDateFin);

  const stats = {
    attente: enAttente.length,
    total: pochettesDuRole.length,
    solde: pochettesDuRole.filter(p => p.statut === 'solde').length,
    rejete: pochettesDuRole.filter(p => p.statut === 'rejete').length,
    montantTraite: pochettesDuRole.filter(p => p.statut === 'solde').reduce((s, p) => s + (Number(p.montant) || 0), 0),
    p1Count: enAttente.filter(p => p.priorite === 'P1').length,
  };

  const heure = new Date().getHours();
  const salutation = heure < 12 ? 'Bonjour' : heure < 18 ? 'Bon apres-midi' : 'Bonsoir';

  const groupesFiliale = enAttente.reduce((acc, p) => {
    const filialeId = p.filiale?.id || p.filiale?._id || 'inconnu';
    const filiale = p.filiale || { id: filialeId, code: '?', nom: 'Filiale inconnue', couleur: '#2980B9' };
    if (!acc[filialeId]) acc[filialeId] = { filiale, nb: 0, urgents: 0 };
    acc[filialeId].nb++;
    if (p.priorite === 'P1') acc[filialeId].urgents++;
    return acc;
  }, {});

  return (
    <div style={s.page}>
      <div data-entete style={s.topbar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src="/logo.png" alt="JETA" style={{ height: 30 }} onError={e => { e.target.style.display = 'none'; }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>JETA GED</div>
            <div style={{ fontSize: 11, color: '#8899AA' }}>{user?.roles?.[0]?.filiale?.nom || 'JETA Groupe'}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {stats.attente > 0 && (
            <div onClick={() => navigate('/parapheur-role')}
              style={{ background: '#C0392B', borderRadius: 20, padding: '6px 16px', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <IconeCloche taille={14} />{stats.attente} en attente
              {stats.p1Count > 0 && <span style={{ background: 'rgba(255,255,255,0.25)', padding: '1px 8px', borderRadius: 10, fontSize: 11 }}><IconeFeu taille={11} /> {stats.p1Count} urgent{stats.p1Count > 1 ? 's' : ''}</span>}
            </div>
          )}
          <ExportExcel role={roleUser} />
          <MenuProfil couleurAvatar={config.color} />
        </div>
      </div>

      <div style={s.body}>
        <div style={{ ...s.hero, ...(estMobile ? s.heroMobile : null), background: 'linear-gradient(135deg, ' + config.color + ', ' + config.color + 'CC)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', marginBottom: 6 }}>{salutation}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#fff', marginBottom: 6 }}>{user?.prenom} {user?.nom}</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', marginBottom: 16 }}>{config.description}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {config.tips.map((tip, i) => (
                <span key={i} style={{ fontSize: 11, background: 'rgba(255,255,255,0.2)', color: '#fff', padding: '4px 12px', borderRadius: 20, fontWeight: 500 }}>
                  {i + 1}. {tip}
                </span>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0, alignItems: estMobile ? 'stretch' : 'flex-end', width: estMobile ? '100%' : 'auto' }}>
            <button onClick={() => navigate('/parapheur-role')}
              style={{ padding: '14px 24px', borderRadius: 12, border: 'none', background: '#fff', color: config.color, fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 15px rgba(0,0,0,0.15)', whiteSpace: 'nowrap', width: estMobile ? '100%' : 'auto' }}>
              <config.icon taille={16} /> Ouvrir mon parapheur
              {stats.attente > 0 && <span style={{ marginLeft: 8, background: '#C0392B', color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: 12 }}>{stats.attente}</span>}
            </button>
            {stats.montantTraite > 0 && (
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '10px 16px', textAlign: estMobile ? 'left' : 'right' }}>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', marginBottom: 3 }}>Montant approuve via votre parapheur</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>{stats.montantTraite.toLocaleString('fr-FR')} FCFA</div>
              </div>
            )}
          </div>
        </div>

        {ROLES_HOLDING.includes(roleUser) ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
            {Object.values(groupesFiliale).sort((a, b) => a.filiale.nom.localeCompare(b.filiale.nom)).map(({ filiale, nb, urgents }) => {
              const filialeId = filiale.id || filiale._id;
              const couleur = filiale.couleur || '#2980B9';
              const isActif = filtreFiliale === filialeId;
              return (
                <div key={filialeId}
                  onClick={() => { setFiltreFiliale(isActif ? '' : filialeId); setOnglet('attente'); }}
                  style={{ background: isActif ? couleur + '12' : '#fff', border: isActif ? '2px solid ' + couleur : urgents > 0 ? '1.5px solid #C0392B' : '1px solid #e5e7eb', borderRadius: 14, padding: '16px 12px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s', boxShadow: isActif ? '0 4px 16px ' + couleur + '25' : '0 2px 6px rgba(0,0,0,0.05)' }}>
                  <div style={{ width: 52, height: 52, borderRadius: 14, background: couleur + '18', border: '1px solid ' + couleur + '40', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px', overflow: 'hidden' }}>
                    {filiale.logo ? (
                      <img src={( process.env.REACT_APP_STATIC_URL || 'http://localhost:5001') + '/' + filiale.logo} alt={filiale.code} style={{ width: 40, height: 40, objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <span style={{ fontSize: 12, fontWeight: 700, color: couleur }}>{filiale.code?.slice(0, 3)}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0D1B2A', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {filiale.nom?.length > 16 ? filiale.code : filiale.nom}
                  </div>
                  <div style={{ fontSize: 10, color: '#888', marginBottom: 12 }}>{filiale.code}</div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: isActif ? couleur : couleur + '20', color: isActif ? '#fff' : couleur, borderRadius: 20, padding: '5px 18px', fontSize: 22, fontWeight: 800, marginBottom: 6 }}>
                    {nb}
                  </div>
                  {urgents > 0 ? (
                    <div style={{ fontSize: 10, color: '#C0392B', fontWeight: 700 }}><IconeFeu taille={11} /> {urgents} urgent{urgents > 1 ? 's' : ''}</div>
                  ) : (
                    <div style={{ fontSize: 10, color: '#888' }}>dossier{nb > 1 ? 's' : ''} en attente</div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={estMobile ? { ...s.statsGrid, gridTemplateColumns: 'repeat(2, 1fr)' } : s.statsGrid}>
            {[
              { label: 'En attente',    value: stats.attente, color: config.color, bg: config.colorLight, icon: config.icon, action: () => navigate('/parapheur-role') },
              { label: 'Total traites', value: stats.total,   color: '#0D1B2A',    bg: '#F5F6FA',         icon: IconeDossier,  action: () => setOnglet('tous') },
              { label: 'Approuves',     value: stats.solde,   color: '#1E8449',    bg: '#EAFAF1',         icon: IconeValide,   action: null },
              { label: 'Rejetes',       value: stats.rejete,  color: '#96281B',    bg: '#FDEDEC',         icon: IconeRefuse,   action: null },
            ].map((st, i) => (
              <div key={i} onClick={st.action || undefined}
                style={{ ...s.statCard, cursor: st.action ? 'pointer' : 'default', borderTop: '3px solid ' + st.color }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{st.label}</div>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: st.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: st.color }}><st.icon taille={19} /></div>
                </div>
                <div style={{ fontSize: 36, fontWeight: 800, color: st.color, letterSpacing: '-0.5px', lineHeight: 1 }}>{st.value}</div>
                {st.action && <div style={{ fontSize: 11, color: st.color, marginTop: 8, fontWeight: 600 }}>Voir →</div>}
              </div>
            ))}
          </div>
        )}

        <div style={s.section}>
          <div style={s.sectionHeader}>
            <div style={s.onglets}>
              {[
                { key: 'attente', label: <><config.icon taille={14} /> En attente</>, count: stats.attente },
                { key: 'tous',    label: <><IconeDossier taille={14} /> Historique</>,   count: stats.total },
              ].map(o => (
                <button key={o.key} onClick={() => setOnglet(o.key)}
                  style={{ ...s.onglet, ...(onglet === o.key ? { borderBottom: '2px solid ' + config.color, color: config.color, background: '#fff', fontWeight: 700 } : {}) }}>
                  {o.label}
                  <span style={{ marginLeft: 6, fontSize: 11, background: onglet === o.key ? config.color : '#e5e7eb', color: onglet === o.key ? '#fff' : '#555', padding: '1px 7px', borderRadius: 10, fontWeight: 600 }}>
                    {o.count}
                  </span>
                </button>
              ))}
            </div>
            <button onClick={charger} style={{ padding: '6px 12px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', color: '#888' }}>
              <IconeRafraichir taille={13} /> Actualiser
            </button>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '12px 20px', background: '#FAFBFC', borderBottom: '0.5px solid #eee', alignItems: 'center' }}>
            <input type="text" placeholder="Rechercher..." value={rechercheTexte} onChange={e => setRechercheTexte(e.target.value)}
              style={{ flex: '1 1 220px', padding: '8px 12px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 12, fontFamily: 'inherit', outline: 'none' }} />
            <select value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 12, fontFamily: 'inherit', background: '#fff' }}>
              <option value="">Tous les statuts</option>
              <option value="en_controle_terrain">Controle terrain</option>
              <option value="en_memo_filiale">Memo Filiale</option>
              <option value="en_signature_dg_filiale">Signature DG</option>
              <option value="envoye_holding">Envoye Holding</option>
              <option value="dans_parapheur_pdg">Parapheur PDG</option>
              <option value="solde">Solde</option>
              <option value="en_correction">En correction</option>
              <option value="en_correction_memo">Correction Memo</option>
              <option value="en_correction_pdg">Correction PDG</option>
            </select>
            <select value={filtreFiliale} onChange={e => setFiltreFiliale(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 12, fontFamily: 'inherit', background: '#fff' }}>
              <option value="">Toutes les filiales</option>
              {filialesDisponibles.map(f => (
                <option key={f.id || f._id} value={f.id || f._id}>{f.code} — {f.nom}</option>
              ))}
            </select>
            <select value={filtrePriorite} onChange={e => setFiltrePriorite(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 12, fontFamily: 'inherit', background: '#fff' }}>
              <option value="">Toute priorite</option>
              <option value="P1">P1 — Urgent</option>
              <option value="P2">P2 — Normal</option>
              <option value="P3">P3 — Faible</option>
            </select>
            <input type="date" value={filtreDateDebut} onChange={e => setFiltreDateDebut(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 12, fontFamily: 'inherit', background: '#fff' }} />
            <span style={{ fontSize: 12, color: '#aaa' }}>→</span>
            <input type="date" value={filtreDateFin} onChange={e => setFiltreDateFin(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 12, fontFamily: 'inherit', background: '#fff' }} />
            {filtresActifs && (
              <button onClick={reinitialiserFiltres}
                style={{ padding: '8px 12px', borderRadius: 8, border: '0.5px solid #C0392B', background: '#FDEDEC', color: '#C0392B', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                <IconeFermer taille={12} /> Reinitialiser
              </button>
            )}
            <span style={{ fontSize: 11, color: '#888', marginLeft: 'auto' }}>
              {docsFiltres.length} resultat{docsFiltres.length !== 1 ? 's' : ''}
            </span>
          </div>

          {loading ? (
            <div style={s.emptyState}><div style={{ marginBottom: 8, color: '#bbb' }}><IconeHorloge taille={28} /></div>Chargement...</div>
          ) : docsFiltres.length === 0 ? (
            <div style={s.emptyState}>
              <div style={{ marginBottom: 14, color: '#ccc' }}>{onglet === 'attente' ? <IconeValide taille={56} /> : <IconeDossier taille={56} />}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0D1B2A', marginBottom: 6 }}>
                {onglet === 'attente' ? 'Vous etes a jour !' : 'Aucun dossier'}
              </div>
              <div style={{ fontSize: 13, color: '#888' }}>
                {onglet === 'attente' ? 'Aucun dossier en attente de traitement' : 'Aucun dossier trouve'}
              </div>
            </div>
          ) : (
            <div>
              <div style={estMobile ? { ...s.tableHeader, gridTemplateColumns: '1fr 90px' } : s.tableHeader}>
                <span>Document</span>
                {!estMobile && <><span>Demandeur</span><span>Filiale</span><span>Montant</span></>}
                <span>Statut</span>
                {!estMobile && <span />}
              </div>
              {docsFiltres.map(p => {
                const statut = STATUTS[p.statut] || STATUTS.brouillon;
                const type = TYPES[p.type_document] || { icon: IconeDocument, color: '#666', label: p.type_document };
                return (
                  <div key={p.id} style={estMobile ? { ...s.tableRow, gridTemplateColumns: '1fr 90px' } : s.tableRow}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
                      <div style={{ width: 38, height: 38, borderRadius: 10, background: type.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', color: type.color, flexShrink: 0 }}><type.icon taille={20} /></div>
                      <div style={{ overflow: 'hidden' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0D1B2A' }}>{p.reference}</div>
                        <div style={{ fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.titre || p.motif || '—'}</div>
                      </div>
                    </div>
                    {!estMobile && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#0D1B2A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontWeight: 700, flexShrink: 0 }}>
                        {p.demandeur?.prenom?.[0]}{p.demandeur?.nom?.[0]}
                      </div>
                      <span style={{ fontSize: 12 }}>{p.demandeur?.prenom} {p.demandeur?.nom}</span>
                    </div>
                    )}
                    {!estMobile && (
                    <span style={{ fontSize: 11, fontWeight: 700, background: '#0D1B2A', color: '#fff', padding: '2px 8px', borderRadius: 6 }}>{p.filiale?.code || '—'}</span>
                    )}
                    {!estMobile && (
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#C0392B' }}>{formatMontant(p.montant)}</span>
                    )}
                    <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: statut.bg, color: statut.color, fontWeight: 600, whiteSpace: 'nowrap' }}>{statut.label}</span>
                    <button onClick={() => navigate('/demandes/' + p.id)}
                      style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid ' + config.color, background: 'transparent', color: config.color, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      Voir →
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#F0F2F5', fontFamily: 'Inter, system-ui, sans-serif' },
  topbar: { background: '#0D1B2A', padding: '12px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  avatar: { width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff' },
  body: { maxWidth: 1000, margin: '0 auto', padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 20 },
  hero: { borderRadius: 16, padding: '28px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20 },
  // Sur mobile la colonne d'actions passe sous la carte d'accueil au lieu de
  // se comprimer a cote d'elle.
  heroMobile: { flexDirection: 'column', alignItems: 'stretch', padding: '22px 18px', gap: 16 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 },
  statCard: { background: '#fff', borderRadius: 14, padding: '18px 20px', border: '0.5px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  section: { background: '#fff', borderRadius: 14, border: '0.5px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 16px 0 0', borderBottom: '0.5px solid #e5e7eb', background: '#F9FAFB' },
  onglets: { display: 'flex' },
  onglet: { padding: '14px 20px', fontSize: 13, fontWeight: 500, cursor: 'pointer', border: 'none', borderBottom: '2px solid transparent', background: 'transparent', color: '#888', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4, transition: '0.15s' },
  tableHeader: { display: 'grid', gridTemplateColumns: '2fr 1.5fr 80px 120px 120px 80px', gap: 12, padding: '10px 20px', background: '#F9FAFB', borderBottom: '0.5px solid #e5e7eb', fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em' },
  tableRow: { display: 'grid', gridTemplateColumns: '2fr 1.5fr 80px 120px 120px 80px', gap: 12, padding: '14px 20px', borderBottom: '0.5px solid #f0f0f0', alignItems: 'center', background: '#fff', transition: '0.1s' },
  emptyState: { padding: '60px 20px', textAlign: 'center', color: '#888', display: 'flex', flexDirection: 'column', alignItems: 'center' },
};
